import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../config/api";
import LoadingIndicator from "../Components/LoadingIndicator";

const Category = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    fetchCategories();
  }, [currentPage, appliedSearch, limit, sortBy, sortOrder]);

  const fetchCategories = async () => {
    if (!API_URL) {
      toast.error("API URL not configured.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        API_URL,
        {
          action: "getActiveCategories",
          page: String(currentPage),
          limit: String(limit),
          search: appliedSearch,
          sortBy,
          sortOrder,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = response.data;

      setRows(data.data || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalCount || 0);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to load categories",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    const userId = localStorage.getItem("userid");
    if (!userId) {
      toast.error("User not found. Please login again.");
      return;
    }
    if (!categoryName.trim()) {
      toast.error("Category is required.");
      return;
    }
    if (!API_URL) {
      toast.error("API URL not configured.");
      return;
    }
    setIsCreating(true);
    try {
      const response = await axios.post(
        API_URL,
        {
          action: "createCategory",
          category: categoryName.trim(),
          userId,
        },
        {
          headers: { "Content-Type": "application/json" },
        },
      );
      toast.success(response.data?.message || "Category created");
      setIsModalOpen(false);
      setCategoryName("");
      await fetchCategories();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to create category";
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  const openDeleteModal = (row) => {
    setSelectedRow(row);
    setIsDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteOpen(false);
    setSelectedRow(null);
  };

  const handleDeleteCategory = async () => {
    if (!API_URL) {
      toast.error("API URL not configured.");
      return;
    }
    const categoryId = selectedRow?._id || selectedRow?.categoryId;
    if (!categoryId) {
      toast.error("Category ID missing.");
      return;
    }
    setIsDeleting(true);
    try {
      const response = await axios.post(
        API_URL,
        {
          action: "deleteCategory",
          categoryId,
        },
        {
          headers: { "Content-Type": "application/json" },
        },
      );
      toast.success(response.data?.message || "Category deleted");
      closeDeleteModal();
      await fetchCategories();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete category";
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(searchTerm);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedSearch("");
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortBy !== column) {
      setSortBy(column);
      setSortOrder("desc");
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
    } else {
      setSortBy("createdAt");
      setSortOrder("desc");
    }

    setCurrentPage(1);
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) {
      return (
        <i
          className="fa-solid fa-sort"
          style={{
            fontSize: "10px",
            marginLeft: "5px",
            opacity: 0.4,
          }}
        />
      );
    }

    return sortOrder === "asc" ? (
      <i
        className="fa-solid fa-sort-up"
        style={{
          fontSize: "10px",
          marginLeft: "5px",
        }}
      />
    ) : (
      <i
        className="fa-solid fa-sort-down"
        style={{
          fontSize: "10px",
          marginLeft: "5px",
        }}
      />
    );
  };

  const getCategoryLabel = (row) =>
    row.category || row.name || row.plan || row.planName || "-";

  const getStatusLabel = (row) =>
    row.status ||
    (row.active === true ? "ACTIVE" : row.active === false ? "INACTIVE" : "-");

  const getStatusClass = (label) => {
    const normalized = String(label || "").toLowerCase();
    if (normalized.includes("inactive") || normalized.includes("expired"))
      return "status-badge inactive";
    if (normalized.includes("active")) return "status-badge active";
    return "status-badge neutral";
  };

  const getModifiedBy = (row) => {
    const by =
      row.updatedBy || row.createdBy || row.lastModifiedBy || row.modifiedBy;
    if (!by) return "-";
    if (typeof by === "string") return by;
    if (typeof by === "object") return by.name || by.email || by._id || "-";
    return String(by);
  };

  const getModifiedAt = (row) =>
    row.updatedAt ||
    row.createdAt ||
    row.lastModifiedAt ||
    row.modifiedAt ||
    "";

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date
      .toLocaleString("en-IN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      })
      .replace(",", " ");
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setCurrentPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));

    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-wrapper">
        <div className="d-flex align-items-center">
          <span className="mr-2">Show</span>

          <select
            className="form-control"
            style={{ width: "80px" }}
            value={limit}
            onChange={handleLimitChange}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <span className="ml-2 mr-3">Entries</span>

          <span className="pagination-info">
            Showing {rows.length > 0 ? (currentPage - 1) * limit + 1 : 0} to{" "}
            {Math.min(currentPage * limit, totalCount)} of {totalCount} entries
          </span>
        </div>

        <div className="pagination-controls">
          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(1)}
          >
            <i className="fas fa-angle-double-left"></i>
          </button>

          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <i className="fas fa-angle-left"></i>
          </button>

          {pages.map((page) => (
            <button
              key={page}
              className={`pagination-btn ${
                currentPage === page ? "active" : ""
              }`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}

          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <i className="fas fa-angle-right"></i>
          </button>

          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(totalPages)}
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="employee-form">
      <div className="content-header subscription-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Category</h1>
            <p>View category details.</p>
          </div>
          <div className="col-sm-6 text-right subscription-header-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setIsModalOpen(true)}
            >
              Add Category
            </button>
          </div>
        </div>
      </div>

      <div className="card employee-content-card">
        <div className="card-body">
           <div className="row mb-3 align-items-end">
            <div className="col-xl-5 col-lg-6 col-md-8 col-sm-12 mb-2">
              <form onSubmit={handleSearch}>
                <label className="font-weight-bold mb-1">Search</label>

                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />

                  <div className="input-group-append">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isLoading}
                    >
                      <i className="fas fa-search"></i>
                    </button>

                    {searchTerm && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleClearSearch}
                        disabled={isLoading}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
          {isLoading ? (
            <div className="employee-loading">
              <LoadingIndicator size={120} />
            </div>
          ) : rows.length === 0 ? (
            <div className="employee-empty">No category data found.</div>
          ) : (
            <div className="employee-table-wrapper">
              <table className="table table-borderless employee-table subscription-table">
                <thead>
                  <tr>
                    <th>Sl No.</th>

                    <th
                      onClick={() => handleSort("category")}
                      style={{ cursor: "pointer" }}
                    >
                      Category {renderSortIcon("category")}
                    </th>

                    <th
                      onClick={() => handleSort("status")}
                      style={{ cursor: "pointer" }}
                    >
                      Status {renderSortIcon("status")}
                    </th>

                    <th
                      onClick={() => handleSort("updatedBy")}
                      style={{ cursor: "pointer" }}
                    >
                      Last Modified By {renderSortIcon("updatedBy")}
                    </th>

                    <th
                      onClick={() => handleSort("updatedAt")}
                      style={{ cursor: "pointer" }}
                    >
                      Last Modified Time {renderSortIcon("updatedAt")}
                    </th>

                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row.id || row.categoryId || idx}>
                      <td>{(currentPage - 1) * limit + idx + 1}</td>
                      <td>{getCategoryLabel(row)}</td>
                      <td>
                        {(() => {
                          const label = getStatusLabel(row);
                          return (
                            <span className={getStatusClass(label)}>
                              {label}
                            </span>
                          );
                        })()}
                      </td>
                      <td>{getModifiedBy(row)}</td>
                      <td>{formatDateTime(getModifiedAt(row))}</td>
                      <td>
                        <div className="employee-actions">
                          <button
                            className="action-icon-btn view"
                            type="button"
                            title="View"
                            onClick={() =>
                              navigate(
                                `/category-view/${row._id || row.categoryId}`,
                              )
                            }
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                          <button
                            className="action-icon-btn delete"
                            type="button"
                            title="Delete"
                            onClick={() => openDeleteModal(row)}
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {renderPagination()}
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="crop-overlay">
          <div className="crop-modal">
            <div className="crop-header">
              <h5>Add Category</h5>
              <button
                type="button"
                className="crop-close"
                onClick={() => setIsModalOpen(false)}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="crop-body">
              <div className="form-group">
                <label className="profile-label" htmlFor="category-name">
                  Category
                </label>
                <input
                  id="category-name"
                  className="form-control"
                  placeholder="Enter category"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                />
              </div>
            </div>
            <div className="crop-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateCategory}
                disabled={isCreating}
              >
                {isCreating ? (
                  <LoadingIndicator size={22} className="inline-loader" />
                ) : (
                  "Add"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteOpen && (
        <div className="crop-overlay">
          <div className="crop-modal confirm-modal">
            <div className="crop-header">
              <h5>Delete Category</h5>
              <button
                type="button"
                className="crop-close"
                onClick={closeDeleteModal}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="crop-body">
              <p className="confirm-message">
                Are you sure want to delete the {getCategoryLabel(selectedRow)}{" "}
                category?
              </p>
            </div>
            <div className="crop-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDeleteModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn confirm-delete-btn"
                onClick={handleDeleteCategory}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <LoadingIndicator size={22} className="inline-loader" />
                ) : (
                  "Yes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Category;
